(() => {
  "use strict";

  const GLOBAL_KEY = "ChiclassyBuilderV1";
  const ROOT_SELECTOR = "[data-chiclassy-builder]";
  const INSTANCE_KEY = "__chiclassyBuilderInstance";
  // At 100% zoom, the canvas height represents 80mm. The pictured 50mm acrylic's
  // former on-screen diameter is now the reference diameter for a 20mm piece.
  // Product measurements are never changed: a 50mm piece renders 2.5x a 20mm one.
  const PREVIEW_REFERENCE_MM = 80;
  const UNKNOWN_SIZE_PREVIEW_MM = 20;
  const SIZE_BANDS = [
    { value: "under-0.5in", label: "Under 0.5in", min: 0, max: 0.5 },
    { value: "0.5-1in", label: "0.5–1in", min: 0.5, max: 1 },
    { value: "1-2in", label: "1–2in", min: 1, max: 2 },
    { value: "2-3in", label: "2–3in", min: 2, max: 3 },
    { value: "3in-plus", label: "3in+", min: 3, max: Infinity }
  ];
  const PRICE_BANDS = [
    { value: "under-0.10", label: "Under $0.10", min: 0, max: 10 },
    { value: "0.10-0.50", label: "$0.10–$0.50", min: 10, max: 50 },
    { value: "0.50-1", label: "$0.50–$1", min: 50, max: 100 },
    { value: "1-5", label: "$1–$5", min: 100, max: 500 },
    { value: "5-plus", label: "$5+", min: 500, max: Infinity }
  ];

  if (window[GLOBAL_KEY]?.initAll) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  class ChiclassyBuilder {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || "USD";
      this.selected = new Map();
      this.canvasZoom = 1;
      this.drawerOpen = false;
      this.drawerIntroduced = false;
      this.acrylicOpen = false;
      this.catalogSequence = 0;
      this.searchQuery = "";
      this.sortOrder = "featured";
      this.visibleLimit = this.getCatalogBatchSize();
      this.catalogNextUrl = this.get("[data-catalog-pagination]")?.dataset.nextUrl || "";
      this.catalogLoading = Boolean(this.catalogNextUrl);
      this.catalogAbort = new AbortController();
      this.activeCategory =
        root.querySelector("[data-category-select]")?.value ||
        root.querySelector("[data-category-tab].is-active")?.dataset.categoryTab ||
        "base";
      this.filterState = {
        subcategory: [],
        color: [],
        material: [],
        size: [],
        price: []
      };
      this.filterDefinitions = [
        { key: "subcategory", label: "Style" },
        { key: "color", label: "Color" },
        { key: "material", label: "Material" },
        { key: "size", label: "Size" },
        { key: "price", label: "Price" }
      ];

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOutsideFilterClick = this.onOutsideFilterClick.bind(this);
      this.onOutsidePreviewClick = this.onOutsidePreviewClick.bind(this);
      this.onAcrylicSaved = this.onAcrylicSaved.bind(this);
      this.onAcrylicOpened = this.onAcrylicOpened.bind(this);
      this.onAcrylicClosed = this.onAcrylicClosed.bind(this);
      this.onOwnCharmSaved = this.onOwnCharmSaved.bind(this);
      this.root.addEventListener("click", this.onClick);
      this.root.addEventListener("change", this.onChange);
      this.root.addEventListener("input", this.onInput);
      this.root.addEventListener("pointerdown", this.onPointerDown);
      this.root.addEventListener("pointermove", this.onPointerMove);
      this.root.addEventListener("pointerup", this.onPointerUp);
      this.root.addEventListener("pointercancel", this.onPointerUp);
      document.addEventListener("keydown", this.onKeydown);
      document.addEventListener("click", this.onOutsideFilterClick);
      // Close before delegated product actions so the same click can still add
      // a piece. A first add or an acrylic save may then explicitly reopen it.
      document.addEventListener("click", this.onOutsidePreviewClick, true);
      document.addEventListener("chiclassy:acrylic-saved", this.onAcrylicSaved);
      document.addEventListener("chiclassy:acrylic-opened", this.onAcrylicOpened);
      document.addEventListener("chiclassy:acrylic-closed", this.onAcrylicClosed);
      this.root.addEventListener("chiclassy:own-charm-saved", this.onOwnCharmSaved);

      this.resizeObserver =
        typeof ResizeObserver === "function"
          ? new ResizeObserver(this.onResize)
          : null;

      const preview = this.get("[data-builder-preview]");
      if (preview && this.resizeObserver) this.resizeObserver.observe(preview);

      this.initializeCards();
      this.initializeFilters();
      this.setCanvasZoom(1);
      this.filterProducts();
      this.render();
      this.root.dataset.builderReady = "true";
      if (this.catalogNextUrl) this.loadCatalogPages();
    }

    get(selector) {
      return this.root.querySelector(selector);
    }

    getAll(selector) {
      return [...this.root.querySelectorAll(selector)];
    }

    getCatalogBatchSize() {
      return window.matchMedia("(max-width: 760px)").matches ? 24 : 36;
    }

    setDrawerOpen(open, restoreFocus = false) {
      this.drawerOpen = Boolean(open);
      this.root.dataset.drawerOpen = String(this.drawerOpen);
      const panel = this.get("[data-builder-drawer]");
      if (panel) panel.hidden = !this.drawerOpen;
      const toggle = this.get("[data-drawer-toggle]");
      toggle?.setAttribute("aria-expanded", String(this.drawerOpen));
      const label = this.get("[data-drawer-toggle-label]");
      if (label) label.textContent = this.drawerOpen ? "Collapse preview ↓" : "Open preview ↑";
      const dock = this.get("[data-builder-dock]");
      if (dock) dock.hidden = this.acrylicOpen;
      if (this.drawerOpen) requestAnimationFrame(() => this.renderPreview());
      else if (restoreFocus) toggle?.focus({ preventScroll: true });
    }

    setDrawerTab(tab) {
      if (!["preview", "selection"].includes(tab)) return;
      const panel = this.get("[data-builder-drawer]");
      if (panel) panel.dataset.drawerTab = tab;
      this.getAll("[data-drawer-tab]").filter((element) => element.tagName === "BUTTON").forEach((button) => {
        button.setAttribute("aria-pressed", String(button.dataset.drawerTab === tab));
      });
      if (tab === "preview") requestAnimationFrame(() => this.renderPreview());
    }

    onInput(event) {
      if (!event.target.matches("[data-catalog-search]")) return;
      this.searchQuery = event.target.value.trim().toLowerCase();
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.filterProducts(), 120);
    }

    async loadCatalogPages() {
      if (this.catalogFetchBusy || !this.catalogNextUrl) return;
      this.catalogFetchBusy = true;
      this.catalogLoading = true;
      this.catalogLoadFailed = false;
      this.filterProducts(false);
      const visited = new Set();
      try {
        while (this.catalogNextUrl && !this.catalogAbort.signal.aborted) {
          const url = new URL(this.catalogNextUrl, window.location.href);
          if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error("Unable to load the next collection page.");
          visited.add(url.href);
          url.searchParams.delete("sections");
          url.searchParams.set("section_id", this.root.dataset.sectionId);
          const response = await fetch(url.href, { signal: this.catalogAbort.signal });
          if (!response.ok) throw new Error("Unable to load the next collection page.");
          const documentFragment = new DOMParser().parseFromString(await response.text(), "text/html");
          const nextRoot = documentFragment.querySelector(ROOT_SELECTOR);
          if (!nextRoot) throw new Error("Collection page is unavailable.");
          const ids = new Set(this.getAll("[data-product-card]").map((card) => card.dataset.productId));
          const cards = [...nextRoot.querySelectorAll("[data-product-card]")].filter((card) => !ids.has(card.dataset.productId));
          if (!cards.length) throw new Error("No additional collection pieces were returned.");
          this.get(".cb-products").append(...cards);
          this.initializeCards();
          this.catalogNextUrl = nextRoot.querySelector("[data-catalog-pagination]")?.dataset.nextUrl || "";
          this.renderFilterOptions();
          this.syncVariantsToFilters();
          this.updateFilterUI();
          this.filterProducts(false);
          this.renderButtons();
        }
      } catch (error) {
        if (error.name !== "AbortError") this.catalogLoadFailed = true;
      } finally {
        this.catalogFetchBusy = false;
        this.catalogLoading = false;
        if (!this.catalogAbort.signal.aborted) this.filterProducts(false);
      }
    }

    openProductDetails(card) {
      if (!card) return;
      const drawer = this.get("[data-product-details-drawer]");
      const backdrop = this.get("[data-product-details-backdrop]");
      const body = this.get("[data-details-body]");
      const title = this.get("[data-details-title]");
      if (!drawer || !backdrop || !body || !title) return;

      const template = card.querySelector("[data-product-details-template]");
      body.replaceChildren(template?.content.cloneNode(true) || document.createTextNode(""));
      body.querySelectorAll(".cb-details-description").forEach((description) => {
        description.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
        description.textContent = description.textContent.replace(/\r/g, "")
          .split("\n").map((line) => line.trim()).filter(Boolean).join("\n");
      });
      title.textContent = card.querySelector("[data-product-title]")?.textContent.trim() || "Product details";
      this.detailsCard = card;
      this.detailsTrigger = document.activeElement;
      this.updateDetailsPanel(card);
      this.updateDetailsAction(card);

      drawer.hidden = false;
      backdrop.hidden = false;
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("cb-details-open");
      drawer.querySelector("[data-close-product-details]")?.focus();
    }

    closeProductDetails() {
      const drawer = this.get("[data-product-details-drawer]");
      const backdrop = this.get("[data-product-details-backdrop]");
      if (!drawer || !backdrop) return;

      drawer.hidden = true;
      backdrop.hidden = true;
      drawer.setAttribute("aria-hidden", "true");
      document.body.classList.remove("cb-details-open");
      this.detailsTrigger?.focus?.();
      this.detailsCard = null;
      this.detailsTrigger = null;
    }

    updateDetailsPanel(card) {
      if (!card) return;
      const variant = card._activeVariant;
      const price = Number(variant?.dataset.price || 0);
      const priceElement = this.get("[data-details-price]");
      if (priceElement) priceElement.textContent = this.formatMoney(price);
    }

    updateDetailsAction(card) {
      const action = this.get("[data-details-action]");
      if (!action || !card) return;
      const variant = card._activeVariant;
      const available = variant?.dataset.available === "true";
      const isCustom = card.dataset.customAcrylicProduct === "true";
      const selected = !isCustom && Boolean(this.findSelectedKeyForProduct(card.dataset.productId));
      const atAcrylicLimit = isCustom && this.getCustomAcrylicCount() >= 2;

      action.disabled = !available || atAcrylicLimit;
      action.textContent = isCustom
        ? atAcrylicLimit ? "Maximum 2" : "Customize"
        : selected ? "Add another to preview" : "Add to preview";
    }

    onKeydown(event) {
      if (this.get("[data-own-charm-dialog]")?.open) return;
      if (event.key === "Escape") {
        const optionPicker = this.get('[data-product-option-picker][open]');
        if (optionPicker) {
          this.closeVariantPickers();
          optionPicker.querySelector("summary")?.focus({ preventScroll: true });
          return;
        }
        const openTrigger = this.get('[data-filter-trigger][aria-expanded="true"]');
        const hadDetails = Boolean(this.detailsCard);
        this.closeFilterMenus();
        openTrigger?.focus();
        if (this.detailsCard) this.closeProductDetails();
        if (!openTrigger && !hadDetails && this.drawerOpen) this.setDrawerOpen(false, true);
      }
      const dialog = this.detailsCard ? this.get("[data-product-details-drawer]") : null;
      if (event.key !== "Tab" || !dialog) return;
      const focusable = [...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]')]
        .filter((element) => element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }

    setMobileView(view) {
      this.setDrawerOpen(view === "preview");
      if (view === "preview") this.setDrawerTab("preview");
    }

    onOutsideFilterClick(event) {
      const filterBar = this.get("[data-builder-filter]");
      if (!filterBar?.contains(event.target)) this.closeFilterMenus();
      if (!event.target.closest?.("[data-variant-selectors]")) this.closeVariantPickers();
    }

    onOutsidePreviewClick(event) {
      if (this.get("[data-own-charm-dialog]")?.open || event.target.closest?.("[data-own-charm-open]")) return;
      if (!this.drawerOpen || this.acrylicOpen || this.dragState) return;
      const dock = this.get("[data-builder-dock]");
      if (dock && !dock.contains(event.target)) this.setDrawerOpen(false);
    }

    onChange(event) {
      if (event.target.matches("[data-catalog-sort]")) {
        this.sortOrder = event.target.value;
        this.filterProducts();
        return;
      }
      if (event.target.matches("[data-category-select]")) {
        this.setCategory(event.target.value);
      }

      const filterOption = event.target.closest("[data-builder-filter-option]");
      if (filterOption && this.root.contains(filterOption)) {
        const key = filterOption.dataset.builderFilterOption;
        if (!Object.prototype.hasOwnProperty.call(this.filterState, key)) return;
        const values = new Set(this.filterState[key]);
        if (filterOption.checked) values.add(filterOption.value);
        else values.delete(filterOption.value);
        this.filterState[key] = [...values];
        this.syncVariantsToFilters();
        this.updateFilterUI();
        this.filterProducts();
      }
    }

    onClick(event) {
      const editOwn = event.target.closest("[data-edit-own-charm]");
      if (editOwn) {
        const item = this.selected.get(editOwn.dataset.editOwnCharm || this.activePreviewKey);
        if (item?.previewOnly) this.root.dispatchEvent(new CustomEvent("chiclassy:own-charm-edit", { detail: { item } }));
        return;
      }
      const optionSummary = event.target.closest("[data-product-option-summary]");
      if (optionSummary) {
        const picker = optionSummary.closest("[data-product-option-picker]");
        this.closeVariantPickers(picker);
        requestAnimationFrame(() => this.positionVariantPicker(picker));
        return;
      }
      const optionChoice = event.target.closest("[data-product-option-choice]");
      if (optionChoice) {
        this.selectCardOption(optionChoice);
        return;
      }
      if (event.target.closest("[data-drawer-toggle]")) {
        this.setDrawerOpen(!this.drawerOpen);
        return;
      }
      if (event.target.closest("[data-drawer-collapse]")) {
        this.setDrawerOpen(false, true);
        return;
      }
      const drawerTab = event.target.closest("button[data-drawer-tab]");
      if (drawerTab) {
        this.setDrawerTab(drawerTab.dataset.drawerTab);
        return;
      }
      const enlarge = event.target.closest("[data-drawer-enlarge]");
      if (enlarge) {
        const large = this.root.dataset.drawerSize !== "large";
        this.root.dataset.drawerSize = large ? "large" : "compact";
        enlarge.setAttribute("aria-pressed", String(large));
        enlarge.setAttribute("aria-label", large ? "Reduce preview" : "Enlarge preview");
        enlarge.textContent = large ? "Reduce" : "Enlarge";
        requestAnimationFrame(() => this.renderPreview());
        return;
      }
      if (event.target.closest("[data-catalog-more]")) {
        this.visibleLimit += this.getCatalogBatchSize();
        this.filterProducts(false);
        return;
      }
      if (event.target.closest("[data-catalog-retry]")) {
        this.loadCatalogPages();
        return;
      }
      if (event.target.closest("[data-reset-catalog]")) {
        this.searchQuery = "";
        const search = this.get("[data-catalog-search]");
        if (search) search.value = "";
        this.clearFilters();
        return;
      }
      const viewButton = event.target.closest("[data-builder-view]");
      if (viewButton) { this.setMobileView(viewButton.dataset.builderView); return; }
      const filterTrigger = event.target.closest("[data-filter-trigger]");
      if (filterTrigger && this.root.contains(filterTrigger)) {
        const key = filterTrigger.dataset.filterTrigger;
        const menu = this.get(`[data-filter-menu="${key}"]`);
        const shouldOpen = Boolean(menu?.hidden);
        this.closeFilterMenus();
        if (menu) {
          menu.hidden = !shouldOpen;
          filterTrigger.setAttribute("aria-expanded", String(shouldOpen));
          if (shouldOpen) this.positionFilterMenu(menu);
        }
        return;
      }

      const clearFilterButton = event.target.closest("[data-clear-filter]");
      if (clearFilterButton && this.root.contains(clearFilterButton)) {
        this.clearFilter(clearFilterButton.dataset.clearFilter, clearFilterButton.dataset.filterValue);
        return;
      }

      if (event.target.closest("[data-clear-filters]")) {
        this.clearFilters();
        return;
      }

      const previewControl = event.target.closest("[data-preview-control]");
      if (previewControl && this.root.contains(previewControl)) {
        this.handlePreviewControl(previewControl.dataset.previewControl);
        return;
      }

      const canvasZoom = event.target.closest("[data-canvas-zoom]");
      if (canvasZoom && this.root.contains(canvasZoom)) {
        const direction = canvasZoom.dataset.canvasZoom === "in" ? 1 : -1;
        this.setCanvasZoom(this.canvasZoom + direction * 0.25);
        return;
      }

      if (event.target.matches("[data-builder-preview]")) {
        if (this.ignoreNextPreviewClick) {
          this.ignoreNextPreviewClick = false;
          return;
        }
        this.clearPreviewSelection();
        return;
      }

      const detailsTrigger = event.target.closest("[data-product-details]");
      if (detailsTrigger && this.root.contains(detailsTrigger)) {
        this.openProductDetails(detailsTrigger.closest("[data-product-card]"));
        return;
      }

      if (event.target.closest("[data-close-product-details]")) {
        this.closeProductDetails();
        return;
      }

      if (event.target.matches("[data-product-details-backdrop]")) {
        this.closeProductDetails();
        return;
      }

      const detailsAction = event.target.closest("[data-details-action]");
      if (detailsAction && this.detailsCard) {
        if (this.detailsCard.dataset.customAcrylicProduct === "true") {
          const card = this.detailsCard;
          this.closeProductDetails();
          this.openAcrylicCustomizer(card);
        } else {
          this.toggleProduct(this.detailsCard);
          this.updateDetailsAction(this.detailsCard);
        }
        return;
      }

      const categoryButton = event.target.closest("[data-category-tab]");
      if (categoryButton && this.root.contains(categoryButton)) {
        this.setCategory(categoryButton.dataset.categoryTab);
        return;
      }

      const swatch = event.target.closest("[data-variant-choice]");
      if (swatch && this.root.contains(swatch)) {
        this.selectVariant(swatch);
        return;
      }

      const addButton = event.target.closest("[data-add-product]");
      if (addButton && this.root.contains(addButton)) {
        this.toggleProduct(addButton.closest("[data-product-card]"));
        return;
      }

      const acrylicTrigger = event.target.closest("[data-custom-acrylic-trigger]");
      if (acrylicTrigger && this.root.contains(acrylicTrigger)) {
        this.openAcrylicCustomizer(acrylicTrigger.closest("[data-product-card]"));
        return;
      }

      const quantityAction = event.target.closest("[data-quantity-action]");
      if (quantityAction && this.root.contains(quantityAction)) {
        const delta = quantityAction.dataset.quantityAction === "increase" ? 1 : -1;
        this.adjustProductQuantity(quantityAction.closest("[data-product-card]"), delta);
        return;
      }

      const removeButton = event.target.closest("[data-remove-selection]");
      if (removeButton && this.root.contains(removeButton)) {
        this.selected.delete(removeButton.dataset.removeSelection);
        if (this.activePreviewKey === removeButton.dataset.removeSelection) this.clearPreviewSelection();
        this.render();
        return;
      }

      if (event.target.closest("[data-reset-builder]")) {
        this.selected.clear();
        this.drawerIntroduced = false;
        this.activePreviewKey = null;
        this.setCanvasZoom(1);
        this.render();
        return;
      }

      if (event.target.closest("[data-builder-add]")) {
        this.addToCart();
      }
    }

    onResize() {
      this.getAll("[data-filter-menu]:not([hidden])").forEach((menu) => this.positionFilterMenu(menu));
      this.getAll("[data-product-option-picker][open]").forEach((picker) => this.positionVariantPicker(picker));
      if (this.selected.size && this.get("[data-builder-preview]")?.clientWidth) this.renderPreview();
    }

    onOwnCharmSaved(event) {
      const detail = event.detail || {};
      if (!detail.previewImage || !(detail.widthMm > 0 && detail.heightMm > 0)) return;
      const existing = detail.key ? this.selected.get(detail.key) : null;
      // An uploaded reference is never a Shopify variant or a custom acrylic order.
      const key = existing?.previewOnly ? existing.key : `own:${this.makeId()}`;
      const item = {
        ...existing, key, ownCharm: true, previewOnly: true, title: "Your own charm",
        widthMm: detail.widthMm, heightMm: detail.heightMm, price: 0, scale: 1,
        previewImage: detail.previewImage, ownCharmState: detail.state,
        variantTitle: `${detail.widthMm.toFixed(1)} × ${detail.heightMm.toFixed(1)} mm`,
        placed: true, sizeEstimated: false, layer: existing?.layer ?? 55,
        x: existing?.x ?? 45, y: existing?.y ?? 30,
        rotation: existing?.rotation ?? 0, flipX: existing?.flipX ?? false, flipY: existing?.flipY ?? false
      };
      this.selected.set(key, item);
      this.activePreviewKey = key;
      this.drawerIntroduced = true;
      this.setDrawerOpen(true);
      this.setDrawerTab("preview");
      this.render();
      this.showBuilderMessage("Your own charm is for preview only and will not be included in your order.");
    }

    onAcrylicSaved(event) {
      const item = event.detail;
      if (!item || !item.customAcrylic) return;

      if (this.getCustomAcrylicCount() >= 2) {
        this.dispatchAcrylicCount();
        return;
      }

      const key = `acrylic:${this.makeId()}`;
      const acrylicItem = {
        ...item,
        key,
        previewImage: item.artworkFile instanceof File
          ? URL.createObjectURL(item.artworkFile)
          : item.previewImage,
        category: "charm",
        customAcrylic: true,
        available: true,
        quantity: 1,
        layer: Number(item.layer || 60) + this.getCustomAcrylicCount(),
        artworkX: Number(item.x || 0),
        artworkY: Number(item.y || 0),
        artworkScale: Number(item.scale || 1),
        sizeEstimated: !(Number(item.widthMm || 0) > 0 || Number(item.heightMm || 0) > 0),
        x: 0,
        y: 0,
        placed: false,
        scale: 1,
        rotation: 0,
        flipX: false,
        flipY: false
      };

      if (!this.insertItem(acrylicItem)) {
        this.showBuilderMessage("Maximum 2 custom acrylic pieces.");
        return;
      }
      this.setCategory("charm");
      this.activePreviewKey = key;
      this.setDrawerTab("preview");
      this.setDrawerOpen(true);
      this.render();
      this.showBuilderMessage("Acrylic added to preview. Drag it from the square into the canvas.");
    }

    onAcrylicOpened() {
      if (this.catalogScrollY === undefined) this.catalogScrollY = window.scrollY;
      this.acrylicOpen = true;
      this.renderTotal();
    }

    onAcrylicClosed() {
      this.acrylicOpen = false;
      this.renderTotal();
      if (this.catalogScrollY !== undefined) {
        window.scrollTo({ top: this.catalogScrollY, behavior: "instant" });
        this.catalogScrollY = undefined;
      }
      if (this.drawerOpen) requestAnimationFrame(() => this.renderPreview());
    }

    openAcrylicCustomizer(card) {
      if (!card || this.getCustomAcrylicCount() >= 2) {
        this.dispatchAcrylicCount();
        return;
      }

      const variant = card._activeVariant;
      if (!variant || variant.dataset.available !== "true") return;

      this.catalogScrollY = window.scrollY;
      document.dispatchEvent(
        new CustomEvent("chiclassy:open-acrylic", {
          detail: {
            productId: card.dataset.productId,
            variantId: variant.dataset.variantId,
            variantTitle: variant.dataset.variantTitle || "",
            price: Number(variant.dataset.price || 0)
          }
        })
      );
    }

    onPointerDown(event) {
      const layer = event.target.closest(".cb-preview-layer, .cb-staging-item");
      if (!layer || !this.root.contains(layer)) return;

      const key = layer.dataset.selectionKey;
      const item = this.selected.get(key);
      const preview = this.get("[data-builder-preview]");
      const scene = this.get("[data-preview-scene]");
      if (!key || !item || !preview || !scene) return;

      const rect = scene.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      this.selectPreviewLayer(key, layer);

      const originStaging = !item.placed;
      const captureTarget = originStaging ? preview : layer;
      this.dragState = {
        key,
        layer,
        preview,
        scene,
        mode: originStaging ? "staging" : "placed",
        originStaging,
        captureTarget,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        sceneWidth: rect.width,
        sceneHeight: rect.height,
        startX: Number(item.x || 0),
        startY: Number(item.y || 0)
      };

      layer.classList.add("is-dragging");
      captureTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    onPointerMove(event) {
      const drag = this.dragState;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const item = this.selected.get(drag.key);
      if (!item) return;

      if (drag.mode === "staging") {
        const staging = this.get("[data-preview-staging]");
        const stageRect = staging?.getBoundingClientRect();
        const previewRect = drag.preview.getBoundingClientRect();
        const moved = Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY) > 6;
        const outsideStage = stageRect && (
          event.clientX < stageRect.left || event.clientX > stageRect.right ||
          event.clientY < stageRect.top || event.clientY > stageRect.bottom
        );
        const insidePreview = event.clientX >= previewRect.left && event.clientX <= previewRect.right &&
          event.clientY >= previewRect.top && event.clientY <= previewRect.bottom;
        if (!moved || !outsideStage || !insidePreview) return;

        item.placed = true;
        this.renderPreview();
        drag.layer = [...drag.scene.querySelectorAll(".cb-preview-layer")]
          .find((candidate) => candidate.dataset.selectionKey === drag.key);
        if (!drag.layer) return;
        drag.mode = "placed";
        this.positionLayerAtPointer(item, drag.layer, event);
        drag.startClientX = event.clientX;
        drag.startClientY = event.clientY;
        drag.startX = item.x;
        drag.startY = item.y;
        drag.layer.classList.add("is-dragging");
        if (item.sizeEstimated) this.showBuilderMessage("Preview uses an estimated 20mm size. Add Builder Width mm for accuracy.");
        event.preventDefault();
        return;
      }

      const deltaX = ((event.clientX - drag.startClientX) / drag.sceneWidth) * 100;
      const deltaY = ((event.clientY - drag.startClientY) / drag.sceneHeight) * 100;
      const bounds = this.getLayerDragBounds(drag.layer, drag.scene);
      item.x = this.clamp(drag.startX + deltaX, bounds.minX, bounds.maxX);
      item.y = this.clamp(drag.startY + deltaY, bounds.minY, bounds.maxY);
      this.syncItemPosition(item, drag.layer);
      event.preventDefault();
    }

    onPointerUp(event) {
      const drag = this.dragState;
      if (!drag || drag.pointerId !== event.pointerId) return;

      drag.layer?.classList.remove("is-dragging");
      if (drag.captureTarget.hasPointerCapture?.(event.pointerId)) {
        drag.captureTarget.releasePointerCapture(event.pointerId);
      }

      if (drag.originStaging) {
        this.ignoreNextPreviewClick = true;
        window.setTimeout(() => { this.ignoreNextPreviewClick = false; }, 0);
      }

      this.dragState = null;
      event.preventDefault();
    }

    getPixelsPerMm(_width, height, referenceMm = PREVIEW_REFERENCE_MM) {
      return Math.max(1, (height || 580) / referenceMm);
    }

    setCanvasZoom(value) {
      this.canvasZoom = this.clamp(Math.round(Number(value) * 100) / 100, 0.5, 2);
      const scene = this.get("[data-preview-scene]");
      if (scene) scene.style.setProperty("--cb-canvas-zoom", String(this.canvasZoom));
      const output = this.get("[data-canvas-zoom-value]");
      if (output) output.textContent = `${Math.round(this.canvasZoom * 100)}%`;
      const out = this.get('[data-canvas-zoom="out"]');
      const zoomIn = this.get('[data-canvas-zoom="in"]');
      if (out) out.disabled = this.canvasZoom <= 0.5;
      if (zoomIn) zoomIn.disabled = this.canvasZoom >= 2;
    }

    positionLayerAtPointer(item, layer, event) {
      const scene = this.get("[data-preview-scene]");
      if (!scene) return;
      const rect = scene.getBoundingClientRect();
      const layerWidth = (layer.offsetWidth / scene.clientWidth) * 100;
      const layerHeight = (layer.offsetHeight / scene.clientHeight) * 100;
      const bounds = this.getLayerDragBounds(layer, scene);
      item.x = this.clamp(((event.clientX - rect.left) / rect.width) * 100 - layerWidth / 2, bounds.minX, bounds.maxX);
      item.y = this.clamp(((event.clientY - rect.top) / rect.height) * 100 - layerHeight / 2, bounds.minY, bounds.maxY);
      this.syncItemPosition(item, layer);
    }

    getLayerDragBounds(layer, scene) {
      const width = (layer.offsetWidth / scene.clientWidth) * 100;
      const height = (layer.offsetHeight / scene.clientHeight) * 100;
      // The scene scales around its center. Zooming out exposes space beyond
      // its original 0–100% bounds, so a piece must be able to reach that
      // newly visible space without being clipped at the old canvas edge.
      const zoomOutMargin = Math.max(0, (1 / this.canvasZoom - 1) * 50);
      const zoomInOverflow = Math.max(0, (this.canvasZoom - 1) * 20);
      const extraX = zoomOutMargin + Math.min(zoomInOverflow, width * 0.5);
      const extraY = zoomOutMargin + Math.min(zoomInOverflow, height * 0.5);
      return {
        minX: -extraX,
        maxX: Math.max(-extraX, 100 - width + extraX),
        minY: -extraY,
        maxY: Math.max(-extraY, 100 - height + extraY)
      };
    }

    syncItemPosition(item, layer) {
      if (layer) {
        layer.style.left = `${item.x}%`;
        layer.style.top = `${item.y}%`;
        layer.style.transform = this.getItemTransform(item);
      }
    }

    getItemTransform(item) {
      const flipX = item.flipX ? -1 : 1;
      const flipY = item.flipY ? -1 : 1;
      return `rotate(${Number(item.rotation || 0)}deg) scaleX(${flipX}) scaleY(${flipY})`;
    }

    selectPreviewLayer(key, layer) {
      this.activePreviewKey = key;
      this.getAll(".cb-preview-layer, .cb-staging-item").forEach((candidate) => {
        candidate.classList.toggle("is-selected", candidate === layer);
      });
      this.updatePreviewControls();
    }

    clearPreviewSelection() {
      this.activePreviewKey = null;
      this.getAll(".cb-preview-layer, .cb-staging-item").forEach((layer) => layer.classList.remove("is-selected"));
      this.updatePreviewControls();
    }

    updatePreviewControls() {
      const controls = this.get("[data-preview-controls]");
      const label = this.get("[data-preview-selected]");
      const item = this.activePreviewKey ? this.selected.get(this.activePreviewKey) : null;
      if (!controls) return;
      controls.hidden = false;
      if (label) label.textContent = item?.placed ? item.title : "Select a piece to rotate or flip";
      const ownEdit = this.get("[data-edit-own-charm]");
      if (ownEdit) ownEdit.hidden = !item?.ownCharm;
      this.getAll("[data-preview-control]").forEach((button) => {
        button.disabled = !item?.placed;
      });
    }

    handlePreviewControl(action) {
      const item = this.activePreviewKey ? this.selected.get(this.activePreviewKey) : null;
      if (!item?.placed) return;

      if (action === "rotate-left") item.rotation = this.normalizeRotation(item.rotation - 15);
      if (action === "rotate-right") item.rotation = this.normalizeRotation(item.rotation + 15);
      if (action === "flip-horizontal") item.flipX = !item.flipX;
      if (action === "flip-vertical") item.flipY = !item.flipY;
      if (action === "delete") {
        this.selected.delete(this.activePreviewKey);
        this.clearPreviewSelection();
        this.render();
        return;
      }

      this.renderPreview();
    }

    normalizeRotation(value) {
      const rotation = Number(value || 0) % 360;
      return rotation > 180 ? rotation - 360 : rotation < -180 ? rotation + 360 : rotation;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    showBuilderMessage(message) {
      const element = this.get("[data-builder-message]");
      if (!element) return;
      element.textContent = message || "";
      element.hidden = !message;
      window.clearTimeout(this.messageTimer);
      if (message) {
        this.messageTimer = window.setTimeout(() => {
          element.hidden = true;
          element.textContent = "";
        }, 3200);
      }
    }

    initializeCards() {
      this.getAll("[data-product-card]").forEach((card) => {
        if (card._activeVariant) return;
        card._catalogIndex = this.catalogSequence++;
        const firstAvailable =
          card.querySelector('[data-variant-choice][data-available="true"]') ||
          card.querySelector("[data-variant-choice]");

        this.initializeVariantPickers(card);
        if (firstAvailable) this.updateCardVariant(card, firstAvailable);
      });
    }

    initializeVariantPickers(card) {
      const variants = [...card.querySelectorAll("[data-variant-choice]")];
      if (variants.length < 2 || card._variantOptionGroups) return;
      const groups = [1, 2, 3].map((index) => {
        const name = variants[0].dataset[`option${index}Name`] || "";
        const normalized = this.normalizeFilterValue(name);
        const kind = ["size", "diameter", "尺寸", "大小"].includes(normalized) ? "size"
          : ["color", "colour", "颜色"].includes(normalized) ? "color" : "option";
        const values = [...new Set(variants.map((variant) => variant.dataset[`option${index}Value`])
          .filter((value) => value && value !== "Default Title"))];
        return { index, name, kind, values };
      }).filter((group) => group.name && group.values.length);
      // Single-option products keep their existing picker. Multi-option products
      // retain every actual variant as a hidden data record, not a duplicate UI choice.
      if (!groups.some((group) => group.kind === "size") || !groups.some((group) => group.kind === "color")) return;
      const order = { size: 0, color: 1, option: 2 };
      groups.sort((a, b) => order[a.kind] - order[b.kind] || a.index - b.index);
      const original = card.querySelector("[data-color-picker]") || card.querySelector(".cb-swatches");
      if (!original) return;
      card._variantOptionGroups = groups;
      original.hidden = true;
      const selectors = document.createElement("div");
      selectors.className = "cb-variant-selectors";
      selectors.dataset.variantSelectors = "";
      original.before(selectors);
      groups.forEach((group) => {
        const picker = document.createElement("details");
        picker.className = "cb-option-picker";
        picker.dataset.productOptionPicker = String(group.index);
        picker.dataset.optionKind = group.kind;
        const summary = document.createElement("summary");
        summary.className = "cb-option-picker__summary";
        summary.dataset.productOptionSummary = "";
        const copy = document.createElement("span");
        copy.className = "cb-option-picker__copy";
        const name = document.createElement("span");
        name.textContent = group.kind === "size" ? "Size" : group.kind === "color" ? "Colors" : group.name;
        const selection = document.createElement("span");
        selection.className = "cb-option-picker__selection";
        selection.dataset.optionSelection = "";
        copy.append(name, selection);
        const icon = document.createElement("span");
        icon.className = "cb-option-picker__icon";
        icon.setAttribute("aria-hidden", "true");
        icon.textContent = "+";
        summary.append(copy, icon);
        const choices = document.createElement("div");
        choices.className = "cb-option-picker__choices";
        choices.setAttribute("role", "group");
        choices.setAttribute("aria-label", `Choose ${group.name}`);
        group.values.forEach((value) => {
          const choice = document.createElement("button");
          choice.type = "button";
          choice.className = "cb-option-choice";
          choice.dataset.productOptionChoice = "";
          choice.dataset.optionIndex = String(group.index);
          choice.dataset.optionValue = value;
          if (group.kind === "color") {
            const thumbnail = document.createElement("span");
            thumbnail.className = "cb-option-choice__thumbnail";
            thumbnail.setAttribute("aria-hidden", "true");
            choice.append(thumbnail);
          }
          const label = document.createElement("span");
          label.className = "cb-option-choice__label";
          label.textContent = value;
          const status = document.createElement("span");
          status.className = "cb-option-choice__status";
          choice.append(label, status);
          choices.append(choice);
        });
        picker.append(summary, choices);
        selectors.append(picker);
        group.picker = picker;
      });
    }

    getCardOptionCandidates(card, group, value) {
      const preceding = card._variantOptionGroups.slice(0, card._variantOptionGroups.indexOf(group));
      return [...card.querySelectorAll("[data-variant-choice]")].filter((variant) =>
        variant.dataset[`option${group.index}Value`] === value && preceding.every((previous) =>
          variant.dataset[`option${previous.index}Value`] === card._activeVariant?.dataset[`option${previous.index}Value`]));
    }

    getVariantImage(card, variant, preview = false) {
      if (!variant) return "";
      const property = preview ? "variantPreviewImage" : "variantImage";
      if (variant.dataset[property]) return variant.dataset[property];
      // A color's picture is often assigned to just one size in Shopify. Reuse
      // it only across sizes, keeping color and any other options identical.
      if (Object.prototype.hasOwnProperty.call(variant.dataset, property) && card._variantOptionGroups) {
        const sameOptions = card._variantOptionGroups.filter((group) => group.kind !== "size");
        const source = [...card.querySelectorAll("[data-variant-choice]")].find((candidate) =>
          candidate.dataset[property] && sameOptions.every((group) =>
            candidate.dataset[`option${group.index}Value`] === variant.dataset[`option${group.index}Value`]));
        if (source) return source.dataset[property];
      }
      return preview ? variant.dataset.preview || "" : variant.dataset.cardImage || variant.dataset.preview || "";
    }

    syncVariantPickers(card) {
      if (!card._variantOptionGroups || !card._activeVariant) return;
      card._variantOptionGroups.forEach((group) => {
        const selected = card._activeVariant.dataset[`option${group.index}Value`] || "";
        group.picker.querySelector("[data-option-selection]").textContent = selected;
        group.picker.querySelectorAll("[data-product-option-choice]").forEach((choice) => {
          const value = choice.dataset.optionValue;
          const candidates = this.getCardOptionCandidates(card, group, value);
          const available = candidates.find((variant) => variant.dataset.available === "true");
          const active = selected === value;
          choice.disabled = !available;
          choice.classList.toggle("is-active", active);
          choice.setAttribute("aria-pressed", String(active));
          const status = available ? "" : candidates.length ? "Sold out" : "Unavailable";
          choice.querySelector(".cb-option-choice__status").textContent = status;
          choice.title = status ? `${value} — ${status}` : value;
          choice.setAttribute("aria-label", choice.title);
          if (group.kind !== "color") return;
          const source = available || candidates[0] || [...card.querySelectorAll("[data-variant-choice]")]
            .find((variant) => variant.dataset[`option${group.index}Value`] === value);
          const thumbnail = choice.querySelector(".cb-option-choice__thumbnail");
          const swatch = source?.style.getPropertyValue("--swatch-color");
          const image = this.getVariantImage(card, source);
          thumbnail.hidden = !swatch && !image;
          if (swatch) {
            thumbnail.replaceChildren();
            thumbnail.style.background = swatch;
          } else if (image) {
            thumbnail.style.background = "";
            let img = thumbnail.querySelector("img");
            if (!img) {
              img = document.createElement("img");
              img.alt = "";
              img.loading = "lazy";
              thumbnail.replaceChildren(img);
            }
            if (img.getAttribute("src") !== image) img.src = image;
          }
        });
      });
    }

    selectCardOption(choice) {
      const card = choice.closest("[data-product-card]");
      if (!card?._variantOptionGroups || choice.disabled) return;
      const group = card._variantOptionGroups.find((option) => option.index === Number(choice.dataset.optionIndex));
      if (!group) return;
      const candidates = this.getCardOptionCandidates(card, group, choice.dataset.optionValue)
        .filter((variant) => variant.dataset.available === "true");
      // Preserve the chosen color when changing size if that purchasable
      // combination exists. Otherwise choose the first available combination.
      const score = (variant) => card._variantOptionGroups.reduce((total, option) => total +
        Number(variant.dataset[`option${option.index}Value`] === card._activeVariant?.dataset[`option${option.index}Value`]), 0);
      candidates.sort((a, b) => score(b) - score(a));
      if (!candidates.length) return;
      this.selectVariant(candidates[0]);
      const next = card._variantOptionGroups[card._variantOptionGroups.indexOf(group) + 1];
      if (next && next.values.length > 1) {
        next.picker.open = true;
        next.picker.querySelector("summary")?.focus({ preventScroll: true });
        requestAnimationFrame(() => this.positionVariantPicker(next.picker));
      } else group.picker.querySelector("summary")?.focus({ preventScroll: true });
    }

    closeVariantPickers(except = null) {
      this.getAll("[data-product-option-picker][open]").forEach((picker) => {
        if (picker !== except) picker.open = false;
      });
    }

    positionVariantPicker(picker) {
      if (!picker?.open) return;
      const menu = picker.querySelector(".cb-option-picker__choices");
      menu.style.transform = "";
      const box = menu.getBoundingClientRect();
      const shift = Math.max(8 - box.left, Math.min(0, window.innerWidth - 8 - box.right));
      if (shift) menu.style.transform = `translateX(${shift}px)`;
    }

    initializeFilters() {
      this.renderFilterOptions();
      this.updateFilterUI();
    }

    normalizeFilterValue(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[–—]/g, "-")
        .replace(/[\s_]+/g, "-");
    }

    splitFilterValues(value) {
      return String(value || "")
        .split(/[|,;]/)
        .map((part) => this.normalizeFilterValue(part))
        .filter(Boolean);
    }

    getProductTitle(card) {
      return card.querySelector("[data-product-title], .cb-product-title")?.textContent.trim() || "";
    }

    getVariantOptionValues(variant, key) {
      const values = [];
      for (let index = 1; index <= 3; index += 1) {
        const name = this.normalizeFilterValue(variant.dataset[`option${index}Name`]);
        const value = variant.dataset[`option${index}Value`];
        if (!value || value === "Default Title") continue;
        if (key === "subcategory" && (name === "style" || name === "subcategory")) values.push(value);
        if (key === "color" && (name === "color" || name === "colour")) values.push(value);
        if (key === "material" && name === "material") values.push(value);
        if (key === "size" && (name === "size" || name === "diameter")) {
          const dimensions = this.parseVariantDimensions(value);
          values.push(dimensions.width > 0
            ? dimensions.height > 0 && dimensions.width !== dimensions.height
              ? `${dimensions.width}x${dimensions.height}mm`
              : `${dimensions.width}mm`
            : value);
        }
      }
      if (key === "color" && !values.length) {
        const title = String(variant.dataset.variantTitle || "").trim();
        if (/^(black|white|blue|red|green|yellow|orange|pink|purple|brown|beige|cream|gold|silver|gray|grey)$/i.test(title)) {
          values.push(title);
        }
      }
      return values;
    }

    inferStyleFromTitle(title) {
      const value = String(title || "").toLowerCase();
      if (/keychain[\s-]*clip.*(?:key[\s-]*ring|keyring)/.test(value)) return "keychain-clip-with-keyring";
      if (/carabiner/.test(value)) return "carabiner-clip";
      if (/key[\s-]*ring|keyring/.test(value)) return "keyring";
      return "";
    }

    inferMaterialFromTitle(title) {
      const value = String(title || "").toLowerCase();
      return ["acrylic", "metal", "leather", "silicone", "resin", "plastic", "wood"].find(
        (material) => new RegExp(`\\b${material}\\b`).test(value)
      ) || "";
    }

    getFilterValues(card, key) {
      if (!card) return [];
      const dataKey = `filter${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      const directValues = this.splitFilterValues(card.dataset[dataKey]);
      const tags = this.splitFilterValues(card.dataset.filterTags);
      const values = [...directValues];
      const title = this.getProductTitle(card);
      const variants = [...card.querySelectorAll("[data-variant-choice]")];

      if (key !== "size" && key !== "price") {
        variants.forEach((variant) => values.push(...this.getVariantOptionValues(variant, key)));
      }

      if (key === "subcategory") {
        tags.forEach((tag) => {
          if (tag.startsWith("subcategory-")) values.push(tag.slice(13));
          if (tag.startsWith("sub-")) values.push(tag.slice(4));
          if (tag.startsWith("style-")) values.push(tag.slice(6));
        });
        if (!values.length) {
          const inferredStyle = this.inferStyleFromTitle(`${title} ${card.dataset.productType || ""}`);
          if (inferredStyle) values.push(inferredStyle);
        }
      }
      if (key === "color") {
        tags.forEach((tag) => {
          if (tag.startsWith("color-")) values.push(tag.slice(6));
        });
      }
      if (key === "material") {
        tags.forEach((tag) => {
          if (tag.startsWith("material-")) values.push(tag.slice(9));
        });
        if (!values.length) {
          const inferredMaterial = this.inferMaterialFromTitle(title);
          if (inferredMaterial) values.push(inferredMaterial);
        }
      }

      if (key === "size") {
        variants.forEach((variant) => {
          const sizeMm = this.getVariantSizeMm(variant, title);
          const sizeInches = sizeMm > 0 ? sizeMm / 25.4 : 0;
          const band = sizeInches > 0 ? this.getBand(SIZE_BANDS, sizeInches) : "";
          if (band) values.push(band);
        });
      }

      if (key === "price") {
        variants.forEach((variant) => {
          const band = this.getBand(PRICE_BANDS, Number(variant.dataset.price));
          if (band) values.push(band);
        });
      }

      return [...new Set(values.map((value) => this.normalizeFilterValue(value)).filter(Boolean))];
    }

    getBand(bands, amount) {
      if (!Number.isFinite(amount) || amount < 0) return "";
      return bands.find((band) => amount >= band.min && amount < band.max)?.value || "";
    }

    getVariantSizeMm(variant, title = "") {
      const dimensions = this.getVariantDimensions(variant, title);
      return Math.max(dimensions.width || 0, dimensions.height || 0, Number(variant.dataset.lengthMm || 0));
    }

    formatFilterLabel(value, key = "") {
      const band = key === "size"
        ? SIZE_BANDS.find((option) => option.value === value)
        : key === "price"
          ? PRICE_BANDS.find((option) => option.value === value)
          : null;
      if (band) return band.label;
      return String(value || "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    renderFilterOptions() {
      this.filterDefinitions.forEach(({ key }) => {
        const control = this.get(`[data-filter-control="${key}"]`);
        const optionsContainer = this.get(`[data-filter-menu="${key}"] [data-filter-options]`);
        if (!control || !optionsContainer) return;

        const cards = this.getAll("[data-product-card]").filter(
          (card) => this.normalizeCategory(card.dataset.category) === this.normalizeCategory(this.activeCategory)
        );
        const availableValues = [...new Set(cards.flatMap((card) => this.getFilterValues(card, key)))];
        const values = key === "size"
          ? SIZE_BANDS.filter((band) => availableValues.includes(band.value)).map((band) => band.value)
          : key === "price"
            ? PRICE_BANDS.filter((band) => availableValues.includes(band.value)).map((band) => band.value)
            : availableValues.sort();
        optionsContainer.replaceChildren();
        control.hidden = false;
        const trigger = control.querySelector("[data-filter-trigger]");
        if (trigger) {
          trigger.disabled = values.length === 0;
          trigger.title = values.length ? "Choose a filter" : "Add product data to enable this filter";
        }
        this.filterState[key] = this.filterState[key].filter((value) => values.includes(value));
        const menu = control.querySelector("[data-filter-menu]");
        if (menu && trigger) {
          menu.id = `${this.root.id}-filter-${key}`;
          trigger.setAttribute("aria-controls", menu.id);
        }

        values.forEach((value) => {
          const label = document.createElement("label");
          label.className = "cb-filter-option";
          const input = document.createElement("input");
          input.type = "checkbox";
          input.value = value;
          input.dataset.builderFilterOption = key;
          input.checked = this.filterState[key].includes(value);
          const text = document.createElement("span");
          text.textContent = this.formatFilterLabel(value, key);
          label.append(input, text);
          optionsContainer.appendChild(label);
        });
      });
    }

    updateFilterUI() {
      const hasFilters = Object.values(this.filterState).some((values) => values.length);
      const clear = this.get("[data-clear-filters]");
      if (clear) clear.hidden = !hasFilters;
      const activeFilters = this.get("[data-active-filters]");
      if (activeFilters) {
        activeFilters.replaceChildren();
        activeFilters.hidden = !hasFilters;
        this.filterDefinitions.forEach(({ key, label }) => {
          this.filterState[key].forEach((selectedValue) => {
            const chip = document.createElement("button");
            chip.type = "button";
            chip.className = "cb-filter-chip";
            chip.dataset.clearFilter = key;
            chip.dataset.filterValue = selectedValue;
            const value = this.formatFilterLabel(selectedValue, key);
            chip.textContent = `${value} ×`;
            chip.setAttribute("aria-label", `Clear ${label}: ${value}`);
            activeFilters.appendChild(chip);
          });
        });
      }
      this.filterDefinitions.forEach(({ key, label }) => {
        const trigger = this.get(`[data-filter-trigger="${key}"]`);
        if (!trigger) return;
        const values = this.filterState[key];
        trigger.classList.toggle("is-active", values.length > 0);
        trigger.setAttribute("aria-label", values.length
          ? `${label}: ${values.map((value) => this.formatFilterLabel(value, key)).join(", ")}`
          : label);
        let count = trigger.querySelector(".cb-filter-badge");
        if (!count) {
          count = document.createElement("span");
          count.className = "cb-filter-badge";
          count.setAttribute("aria-hidden", "true");
          trigger.insertBefore(count, trigger.querySelector(".cb-filter-chevron"));
        }
        count.textContent = values.length ? String(values.length) : "";
        count.hidden = values.length === 0;
        const clear = this.get(`[data-clear-filter="${key}"]`);
        if (clear) clear.disabled = values.length === 0;
      });

      this.getAll("[data-builder-filter-option]").forEach((input) => {
        input.checked = this.filterState[input.dataset.builderFilterOption].includes(input.value);
      });
      this.getAll("[data-filter-menu]:not([hidden])").forEach((menu) => this.positionFilterMenu(menu));
    }

    syncVariantsToFilters() {
      if (!Object.values(this.filterState).some((values) => values.length)) return;

      this.getAll("[data-product-card]").forEach((card) => {
        if (this.normalizeCategory(card.dataset.category) !== this.normalizeCategory(this.activeCategory)) return;
        const title = this.getProductTitle(card);
        if (card._activeVariant?.dataset.available === "true" && this.variantMatchesFilters(card._activeVariant, title)) return;
        const match = [...card.querySelectorAll('[data-variant-choice][data-available="true"]')]
          .find((variant) => this.variantMatchesFilters(variant, title));
        if (match && match !== card._activeVariant) this.selectVariant(match);
      });
    }

    variantMatchesFilters(variant, title) {
      // OR within each group, AND across groups on the same purchasable variant.
      for (const key of ["subcategory", "color", "material"]) {
        if (!this.filterState[key].length) continue;
        const values = this.getVariantOptionValues(variant, key)
          .map((value) => this.normalizeFilterValue(value));
        if (values.length && !values.some((value) => this.filterState[key].includes(value))) return false;
      }
      const sizeMm = this.getVariantSizeMm(variant, title);
      const sizeInches = sizeMm > 0 ? sizeMm / 25.4 : 0;
      if (this.filterState.size.length && (sizeInches <= 0 || !this.filterState.size.includes(this.getBand(SIZE_BANDS, sizeInches)))) {
        return false;
      }
      if (this.filterState.price.length && !this.filterState.price.includes(this.getBand(PRICE_BANDS, Number(variant.dataset.price)))) {
        return false;
      }
      return true;
    }

    clearFilter(key, value) {
      if (!Object.prototype.hasOwnProperty.call(this.filterState, key)) return;
      this.filterState[key] = value === undefined ? [] : this.filterState[key].filter((entry) => entry !== value);
      this.syncVariantsToFilters();
      this.updateFilterUI();
      this.filterProducts();
    }

    clearFilters() {
      Object.keys(this.filterState).forEach((key) => {
        this.filterState[key] = [];
      });
      this.closeFilterMenus();
      this.updateFilterUI();
      this.filterProducts();
    }

    positionFilterMenu(menu) {
      // Keep wrapped dropdowns inside the chooser on narrow screens.
      menu.style.left = "0";
      menu.style.right = "auto";
      menu.style.removeProperty("margin-left");
      const bar = this.get("[data-builder-filter]").getBoundingClientRect();
      const box = menu.getBoundingClientRect();
      const left = Math.max(8, bar.left);
      const right = Math.min(window.innerWidth - 8, bar.right);
      const shift = Math.max(left - box.left, Math.min(0, right - box.right));
      if (shift) menu.style.marginLeft = `${shift}px`;
    }

    closeFilterMenus() {
      this.getAll("[data-filter-menu]").forEach((menu) => {
        menu.hidden = true;
      });
      this.getAll("[data-filter-trigger]").forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });
    }

    setCategory(category) {
      if (!category) return;
      this.activeCategory = category;
      this.getAll("[data-category-tab]").forEach((button) => {
        const isActive = button.dataset.categoryTab === category;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", String(isActive));
      });
      const select = this.get("[data-category-select]");
      if (select) select.value = category;
      this.closeFilterMenus();
      this.renderFilterOptions();
      this.updateFilterUI();
      this.filterProducts();
    }

    filterProducts(resetLimit = true) {
      if (resetLimit) this.visibleLimit = this.getCatalogBatchSize();
      let categoryCount = 0;
      const cards = this.getAll("[data-product-card]");
      const searchTerms = this.searchQuery.split(/\s+/).filter(Boolean);
      const matchingCards = new Set();
      cards.forEach((card) => {
        const inCategory = this.normalizeCategory(card.dataset.category) === this.normalizeCategory(this.activeCategory);
        if (inCategory) categoryCount += 1;
        const matchesFilters = Object.entries(this.filterState).every(([key, values]) => {
          if (!values.length) return true;
          const available = this.getFilterValues(card, key);
          return values.some((value) => available.includes(value));
        });
        const hasVariantFilter = Object.values(this.filterState).some((values) => values.length);
        const variantMatches = !hasVariantFilter || [...card.querySelectorAll("[data-variant-choice]")]
          .some((variant) => this.variantMatchesFilters(variant, this.getProductTitle(card)));
        const searchable = [this.getProductTitle(card), card.dataset.filterTags,
          card.dataset.filterSubcategory, card.dataset.filterMaterial,
          ...[...card.querySelectorAll("[data-variant-choice]")].map((variant) =>
            [variant.dataset.variantTitle, variant.dataset.option1Value, variant.dataset.option2Value, variant.dataset.option3Value].join(" "))
        ].join(" ").toLowerCase();
        if (inCategory && matchesFilters && variantMatches && searchTerms.every((term) => searchable.includes(term))) matchingCards.add(card);
        const variants = [...card.querySelectorAll("[data-variant-choice]")].filter((variant) =>
          this.variantMatchesFilters(variant, this.getProductTitle(card)));
        const available = variants.filter((variant) => variant.dataset.available === "true");
        card._catalogSortPrice = Math.min(...(available.length ? available : variants).map((variant) => Number(variant.dataset.price || 0)));
      });
      cards.sort((a, b) => {
        const difference = this.sortOrder === "price-ascending" ? a._catalogSortPrice - b._catalogSortPrice
          : this.sortOrder === "price-descending" ? b._catalogSortPrice - a._catalogSortPrice : 0;
        return difference || a._catalogIndex - b._catalogIndex;
      });
      let shown = 0;
      const fragment = document.createDocumentFragment();
      cards.forEach((card) => {
        card.hidden = !matchingCards.has(card) || shown >= this.visibleLimit;
        if (!card.hidden) shown += 1;
        fragment.appendChild(card);
      });
      const grid = this.get(".cb-products");
      grid?.appendChild(fragment);
      if (grid) grid.dataset.catalogReady = "true";
      const visibleCount = matchingCards.size;

      const empty = this.get("[data-category-empty]");
      const resultCount = this.get("[data-result-count]");
      if (resultCount) resultCount.textContent = "Showing " + shown + " of " + visibleCount + (visibleCount === 1 ? " piece" : " pieces");
      if (empty) {
        empty.hidden = visibleCount !== 0;
        empty.textContent = this.catalogLoading ? "Loading pieces…"
          : categoryCount && !visibleCount
          ? "No pieces match your search and filters."
          : "No products in this category yet.";
      }
      const more = this.get("[data-catalog-more]");
      if (more) more.hidden = shown >= visibleCount;
      const reset = this.get("[data-reset-catalog]");
      if (reset) reset.hidden = visibleCount !== 0 || !this.searchQuery && !Object.values(this.filterState).some((values) => values.length);
      const status = this.get("[data-catalog-status]");
      if (status) {
        status.hidden = !this.catalogLoading && !this.catalogLoadFailed;
        status.textContent = this.catalogLoadFailed ? "Some pieces could not load. Retry to browse the full collection." : "Loading more pieces…";
      }
      const retry = this.get("[data-catalog-retry]");
      if (retry) retry.hidden = !this.catalogLoadFailed;
    }

    normalizeCategory(value) {
      const normalized = String(value || "").trim().toLowerCase().replace(/[\s_]+/g, "-");
      const aliases = {
        bases: "base",
        keyring: "base",
        keyrings: "base",
        "key-ring": "base",
        "key-rings": "base",
        clasps: "clasp",
        chains: "chain",
        charms: "charm",
        tassels: "tassel"
      };
      return aliases[normalized] || normalized;
    }

    selectVariant(button) {
      const card = button.closest("[data-product-card]");
      if (!card) return;

      this.closeVariantPickers();
      this.updateCardVariant(card, button);
      const colorPicker = card.querySelector("[data-color-picker]");
      if (colorPicker) colorPicker.open = false;
      // A swatch changes only the variant used for the next added unit. It must
      // not recolor or resize pieces the customer has already selected.
      this.renderButtons();
      if (this.detailsCard === card) {
        this.updateDetailsPanel(card);
        this.updateDetailsAction(card);
      }
    }

    updateCardVariant(card, button) {
      card._activeVariant = button;
      card.querySelectorAll("[data-variant-choice]").forEach((choice) => {
        const isActive = choice === button;
        choice.classList.toggle("is-active", isActive);
        choice.setAttribute("aria-pressed", String(isActive));
      });
      this.syncVariantPickers(card);
      const price = Number(button.dataset.price || 0);
      const priceElement = card.querySelector("[data-card-price]");
      if (priceElement) priceElement.textContent = this.formatMoney(price);

      const colorPicker = card.querySelector("[data-color-picker]");
      const colorIndex = Number(colorPicker?.dataset.colorOptionIndex || 0);
      const selectedColor = colorPicker?.querySelector("[data-selected-color]");
      const colorName = colorIndex ? button.dataset[`option${colorIndex}Value`] || "" : "";
      if (selectedColor) selectedColor.textContent = colorName ? ` · ${colorName}` : "";

      const imageUrl = this.getVariantImage(card, button);
      const imageButton = card.querySelector(".cb-product-image");
      if (imageUrl && imageButton) {
        let image = imageButton.querySelector("img");
        if (!image) {
          image = document.createElement("img");
          image.loading = "lazy";
          imageButton.appendChild(image);
        }
        // A Shopify image_tag includes srcset; it must be cleared when a
        // customer switches variants or the browser may keep the old color.
        image.removeAttribute("srcset");
        image.removeAttribute("sizes");
        if (image.getAttribute("src") !== imageUrl) image.src = imageUrl;
        image.alt = [this.getProductTitle(card), colorName || button.dataset.variantTitle]
          .filter(Boolean).join(" — ");
      }

      const addButton = card.querySelector("[data-add-product]");
      const acrylicButton = card.querySelector("[data-custom-acrylic-trigger]");
      if (addButton || acrylicButton) {
        const available = button.dataset.available === "true";
        if (addButton) {
          addButton.disabled = !available;
          if (!available) addButton.textContent = "Sold out";
        }
        if (acrylicButton) acrylicButton.disabled = !available;
      }
    }

    toggleProduct(card) {
      if (!card?._activeVariant) return;

      const item = this.buildItemFromCard(card);
      if (!item.available) return;

      this.insertItem(item);
      this.render();
      const feedback = this.get("[data-selection-announcement]");
      if (feedback) {
        feedback.textContent = `Added ${item.title}${item.variantTitle && item.variantTitle !== "Default Title" ? ` · ${item.variantTitle}` : ""}. Open Preview to arrange your pieces.`;
      }
    }

    adjustProductQuantity(card, delta) {
      if (!card?._activeVariant || card.dataset.customAcrylicProduct === "true") return;
      if (delta > 0) {
        this.toggleProduct(card);
        return;
      }

      // Remove the active variant first; otherwise remove the latest unit of
      // this product. Each unit is an independently draggable preview piece.
      const key = this.findSelectedKeyForProduct(card.dataset.productId, card._activeVariant.dataset.variantId);
      if (!key) return;
      this.selected.delete(key);
      if (this.activePreviewKey === key) this.clearPreviewSelection();
      this.render();
    }

    buildItemFromCard(card) {
      const variant = card._activeVariant;
      const number = (name, fallback = 0) => {
        const parsed = Number(variant.dataset[name]);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const dimensions = this.getVariantDimensions(variant, this.getProductTitle(card));

      return {
        productId: String(card.dataset.productId),
        variantId: String(variant.dataset.variantId),
        title: card.querySelector("[data-product-title], .cb-product-title")?.textContent.trim() || "Untitled item",
        variantTitle: variant.dataset.variantTitle || "",
        variantOptions: [1, 2, 3].map((index) => ({
          name: variant.dataset[`option${index}Name`] || "",
          value: variant.dataset[`option${index}Value`] || ""
        })).filter((option) => option.name && option.value && option.value !== "Default Title"),
        category: card.dataset.category || "other",
        price: number("price"),
        previewImage: this.getVariantImage(card, variant, true).trim(),
        available: variant.dataset.available === "true",
        widthMm: dimensions.width,
        heightMm: dimensions.height,
        lengthMm: number("lengthMm"),
        sizeEstimated: !(dimensions.width > 0 || dimensions.height > 0 || number("lengthMm") > 0),
        sizeMode: variant.dataset.sizeMode || "width_height",
        x: 0,
        y: 0,
        placed: false,
        quantity: 1,
        scale: 1,
        rotation: number("rotation"),
        layer: number("layer", 50),
        flipX: false,
        flipY: false
      };
    }

    parseVariantDimensions(title) {
      const value = String(title || "").trim();
      const pair = value.match(/(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)\s*mm/i);
      if (pair) {
        return { width: Number(pair[1]), height: Number(pair[2]) };
      }

      const single = value.match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (single) {
        const size = Number(single[1]);
        return { width: size, height: size };
      }

      return { width: 0, height: 0 };
    }

    getVariantDimensions(variant, productTitle = "") {
      const width = Number(variant?.dataset.widthMm || 0);
      const height = Number(variant?.dataset.heightMm || 0);
      if (width > 0 || height > 0) {
        return { width: width || height, height: height || width };
      }

      const variantDimensions = this.parseVariantDimensions(variant?.dataset.variantTitle || "");
      if (variantDimensions.width > 0) return variantDimensions;

      const productDimensions = this.parseVariantDimensions(productTitle);
      return productDimensions;
    }

    insertItem(item) {
      if (item.customAcrylic && this.getCustomAcrylicCount() >= 2) return false;
      const key = item.customAcrylic
        ? item.key || `acrylic:${this.makeId()}`
        : `product:${item.productId}:${this.makeId()}`;
      item.key = key;
      this.placeItemInStaging(item);
      this.selected.set(key, item);
      if (!this.drawerIntroduced) {
        this.drawerIntroduced = true;
        this.setDrawerOpen(true);
      }
      return true;
    }

    placeItemInStaging(item) {
      item.x = 0;
      item.y = 0;
      item.placed = false;
      item.flipX = Boolean(item.flipX);
      item.flipY = Boolean(item.flipY);
      item.rotation = Number(item.rotation || 0);
    }

    findSelectedKeyForProduct(productId, preferredVariantId = "") {
      const entries = [...this.selected.entries()].filter(([, item]) =>
        !item.customAcrylic && item.productId === String(productId)
      );
      const preferred = preferredVariantId && [...entries].reverse().find(([, item]) =>
        item.variantId === String(preferredVariantId)
      );
      return preferred?.[0] || entries[entries.length - 1]?.[0] || null;
    }

    getProductQuantity(productId) {
      return [...this.selected.values()].filter((item) =>
        !item.customAcrylic && item.productId === String(productId)
      ).length;
    }

    render() {
      this.renderPreview();
      this.renderSelection();
      this.renderButtons();
      this.renderTotal();
      if (this.detailsCard) {
        this.updateDetailsPanel(this.detailsCard);
        this.updateDetailsAction(this.detailsCard);
      }
    }

    renderPreview() {
      const preview = this.get("[data-builder-preview]");
      const scene = this.get("[data-preview-scene]");
      const staging = this.get("[data-preview-staging]");
      const stagingItems = this.get("[data-staging-items]");
      if (!preview || !scene || !staging || !stagingItems) return;

      scene.querySelectorAll(".cb-preview-layer").forEach((layer) => layer.remove());
      stagingItems.replaceChildren();
      const entries = [...this.selected.entries()].sort(
        ([, a], [, b]) => a.layer - b.layer
      );
      const staged = entries.filter(([, item]) => !item.placed);
      const renderable = entries.filter(([, item]) => item.placed && item.previewImage);
      const empty = this.get("[data-preview-empty]");

      staged.forEach(([key, item]) => {
        const tile = document.createElement("button");
        tile.type = "button";
        tile.className = "cb-staging-item";
        tile.dataset.stagingItem = "";
        tile.dataset.selectionKey = key;
        tile.title = `${item.title} — drag into canvas`;
        tile.setAttribute("aria-label", `Drag ${[item.title, item.variantTitle].filter(Boolean).join(" · ")} into the canvas`);
        if (item.previewImage) {
          const image = document.createElement("img");
          image.src = item.previewImage;
          image.alt = "";
          image.draggable = false;
          tile.appendChild(image);
        } else {
          tile.textContent = item.title;
        }
        if (this.activePreviewKey === key) tile.classList.add("is-selected");
        stagingItems.appendChild(tile);
      });
      staging.hidden = false;

      if (empty) {
        empty.hidden = renderable.length !== 0;
        empty.textContent = staged.length
          ? "Drag a selected piece from the square into the canvas"
          : entries.length
            ? "Preview image is not set for this item"
          : 'Choose a piece above and click "Add to preview" to start your design.';
      }

      const pixelsPerMm = this.getPixelsPerMm(
        scene.clientWidth || preview.clientWidth,
        scene.clientHeight || preview.clientHeight
      );

      renderable.forEach(([key, item]) => {
        const alt = [item.title, item.variantTitle].filter(Boolean).join(" · ");
        const isCustomAcrylic = Boolean(item.customAcrylic);
        const layer = document.createElement(isCustomAcrylic ? "div" : "img");
        layer.className = "cb-preview-layer";
        layer.dataset.selectionKey = key;
        layer.draggable = false;
        layer.setAttribute("role", "img");
        layer.setAttribute("aria-label", `Drag to move ${alt}`);
        if (item.previewOnly) layer.classList.add("cb-preview-layer--own");
        // Zooming out exposes coordinates beyond the original 0–100% scene.
        // Keep those positions when changing tabs, variants or viewport size.
        layer.style.left = `${Number(item.x || 0)}%`;
        layer.style.top = `${Number(item.y || 0)}%`;
        layer.style.zIndex = String(item.layer);
        layer.style.transform = this.getItemTransform(item);

        let image = layer;
        if (isCustomAcrylic) {
          const shapeName = String(item.shape || "circle").toLowerCase();
          const shape = shapeName === "die-cut" || shapeName === "diecut" ? "custom" : shapeName;
          layer.classList.add("cb-preview-acrylic-layer", `is-${shape}`);

          image = document.createElement("img");
          image.className = "cb-preview-acrylic-artwork";
          image.src = item.previewImage;
          image.alt = alt;

          const tab = document.createElement("span");
          tab.className = "cb-preview-acrylic-tab";
          tab.setAttribute("aria-hidden", "true");
          const hole = document.createElement("span");
          hole.className = "cb-preview-acrylic-hole";
          hole.setAttribute("aria-hidden", "true");
          tab.appendChild(hole);
          layer.append(tab);
          if (shape === "custom" && item.outlinePath) {
            layer.style.setProperty("--cb-outline-mask", `url(\"${item.outlineMask || ""}\")`);
            layer.style.setProperty("--cb-bleed-mask", `url(\"${item.bleedMask || ""}\")`);
            layer.style.setProperty("--cb-artwork-width", `${Number(item.outlineArtworkWidth || 100)}%`);
            layer.style.setProperty("--cb-artwork-x", `${Number(item.artworkX || 0)}%`);
            layer.style.setProperty("--cb-artwork-y", `${Number(item.artworkY || 0)}%`);
            layer.style.setProperty("--cb-artwork-scale", String(Number(item.artworkScale || 1)));
            const bleed = document.createElement("span");
            bleed.className = "cb-preview-acrylic-bleed";
            bleed.style.backgroundImage = `url(\"${item.outlinePath}\")`;
            bleed.setAttribute("aria-hidden", "true");
            layer.appendChild(bleed);
          }
          layer.append(image);
        } else {
          image.src = item.previewImage;
          image.alt = alt;
        }

        if (item.previewOnly) {
          layer.style.width = `${item.widthMm * pixelsPerMm}px`;
          layer.style.height = `${item.heightMm * pixelsPerMm}px`;
          // Locked dimensions preserve the trimmed image ratio. If the customer
          // deliberately unlocks proportions, both entered dimensions must apply.
          layer.style.objectFit = "fill";
        } else if (isCustomAcrylic) {
          const customWidth = Number(item.widthMm || item.heightMm || UNKNOWN_SIZE_PREVIEW_MM);
          const customHeight = Number(item.heightMm || item.widthMm || customWidth);
          layer.style.width = `${customWidth * pixelsPerMm}px`;
          layer.style.height = `${customHeight * pixelsPerMm}px`;
        } else if (item.sizeMode === "length" && item.lengthMm > 0) {
          layer.style.width = `${item.lengthMm * pixelsPerMm}px`;
          layer.style.height = "auto";
        } else if (item.widthMm > 0) {
          layer.style.width = `${item.widthMm * pixelsPerMm}px`;
          layer.style.height = "auto";
        } else if (item.heightMm > 0) {
          layer.style.height = `${item.heightMm * pixelsPerMm}px`;
          layer.style.width = "auto";
        } else {
          layer.style.width = `${UNKNOWN_SIZE_PREVIEW_MM * pixelsPerMm}px`;
          layer.style.height = "auto";
          layer.classList.add("cb-preview-layer--fallback-size");
        }

        if (item.sizeEstimated) layer.classList.add("cb-preview-layer--fallback-size");

        scene.appendChild(layer);
      });

      if (this.activePreviewKey && !this.selected.has(this.activePreviewKey)) {
        this.activePreviewKey = null;
      }

      if (this.activePreviewKey) {
        const activeLayer = [...scene.querySelectorAll(".cb-preview-layer")]
          .find((layer) => layer.dataset.selectionKey === this.activePreviewKey);
        if (activeLayer) activeLayer.classList.add("is-selected");
        this.updatePreviewControls();
      } else {
        this.updatePreviewControls();
      }
    }

    renderSelection() {
      const container = this.get("[data-builder-selection]");
      if (!container) return;
      container.replaceChildren();

      if (!this.selected.size) {
        const empty = document.createElement("p");
        empty.className = "cb-selection-empty";
        empty.textContent = "Nothing selected yet.";
        container.appendChild(empty);
        return;
      }

      for (const [key, item] of this.selected.entries()) {
        const row = document.createElement("div");
        row.className = "cb-selected-row";
        const thumbnail = document.createElement("img");
        thumbnail.className = "cb-selected-thumbnail";
        thumbnail.alt = "";
        if (item.previewImage) thumbnail.src = item.previewImage;
        else thumbnail.style.visibility = "hidden";

        const details = document.createElement("div");
        const title = document.createElement("strong");
        title.textContent = item.title;
        details.appendChild(title);

        if (item.variantTitle && item.variantTitle !== "Default Title") {
          const variant = document.createElement("small");
          variant.textContent = item.variantTitle;
          details.appendChild(variant);
        }

        if (item.previewOnly) {
          row.classList.add("cb-selected-row--own");
          const note = document.createElement("small");
          note.className = "cb-own-badge";
          note.textContent = "Your own · Preview only";
          details.appendChild(note);
          const actions = document.createElement("div");
          actions.className = "cb-own-row-actions";
          const edit = document.createElement("button");
          edit.type = "button";
          edit.dataset.editOwnCharm = key;
          edit.textContent = "Edit image / size";
          const remove = document.createElement("button");
          remove.type = "button";
          remove.dataset.removeSelection = key;
          remove.setAttribute("aria-label", "Remove your own charm from preview");
          remove.textContent = "Remove";
          actions.append(edit, remove);
          details.appendChild(actions);
          row.append(thumbnail, details);
          container.appendChild(row);
          continue;
        }

        const priceGroup = document.createElement("div");
        priceGroup.className = "cb-selected-price";
        const price = document.createElement("span");
        const quantity = this.getItemQuantity(item);
        price.textContent = this.formatMoney(item.price * quantity);

        const quantityLabel = document.createElement("small");
        quantityLabel.className = "cb-selected-quantity";
        quantityLabel.textContent = `×${quantity}`;

        const remove = document.createElement("button");
        remove.type = "button";
        remove.dataset.removeSelection = key;
        remove.setAttribute("aria-label", `Remove ${item.title}`);
        remove.textContent = "×";

        priceGroup.append(price, quantityLabel, remove);
        row.append(thumbnail, details, priceGroup);
        container.appendChild(row);
      }
    }

    renderButtons() {
      this.getAll("[data-product-card]").forEach((card) => {
        const button = card.querySelector("[data-add-product]");
        const variant = card._activeVariant;
        if (!variant) return;

        const available = variant.dataset.available === "true";
        if (button) {
          const quantity = this.getProductQuantity(card.dataset.productId);
          const selected = quantity > 0;
          button.hidden = selected;
          button.classList.toggle("is-added", false);
          button.disabled = !available;
          button.textContent = !available ? "Sold out" : "+ Add to preview";
          button.setAttribute("aria-pressed", String(selected));

          const quantityControl = card.querySelector("[data-quantity-control]");
          const quantityElement = card.querySelector("[data-product-quantity]");
          if (quantityControl) {
            quantityControl.hidden = !selected;
            const increase = quantityControl.querySelector('[data-quantity-action="increase"]');
            if (increase) increase.disabled = !available;
          }
          if (quantityElement) quantityElement.textContent = String(quantity);
        }

        const acrylicButton = card.querySelector("[data-custom-acrylic-trigger]");
        if (acrylicButton) {
          const available = variant.dataset.available === "true";
          const count = this.getCustomAcrylicCount();
          acrylicButton.disabled = !available || count >= 2;
          acrylicButton.textContent = count >= 2 ? "Maximum 2" : count ? "Add another acrylic" : "Customize";
          acrylicButton.setAttribute("aria-pressed", "false");
        }
      });
    }

    renderTotal() {
      const purchasable = this.getPurchasableItems();
      const total = purchasable.reduce(
        (sum, item) => sum + item.price * this.getItemQuantity(item),
        0
      );
      const count = purchasable.reduce(
        (sum, item) => sum + this.getItemQuantity(item),
        0
      );
      this.getAll("[data-builder-total]").forEach((element) => { element.textContent = this.formatMoney(total); });
      this.getAll("[data-builder-count]").forEach((element) => { element.textContent = `${count} ${count === 1 ? "item" : "items"}`; });
      this.getAll("[data-builder-add]").forEach((button) => { button.disabled = count === 0 || Boolean(this.cartBusy); });
      const dock = this.get("[data-builder-dock]");
      if (dock) dock.hidden = this.acrylicOpen;
      this.root.classList.toggle("has-selection", count > 0);
      const dockHint = this.get("[data-dock-hint]");
      const ownCount = [...this.selected.values()].filter((item) => item.previewOnly).length;
      if (dockHint) {
        dockHint.hidden = count > 0 && !ownCount;
        dockHint.textContent = ownCount
          ? `${ownCount} own ${ownCount === 1 ? "charm" : "charms"} · Preview only, not included in your order.`
          : "Choose your pieces. Preview before you buy.";
      }
      this.getAll("[data-own-charm-count]").forEach((element) => {
        element.hidden = ownCount === 0;
        element.textContent = `${ownCount} own ${ownCount === 1 ? "charm" : "charms"} · Preview only`;
      });
      const dockSummary = this.get("[data-dock-summary]");
      if (dockSummary) dockSummary.hidden = count === 0;
      if (!count && !this.cartBusy) {
        this.getAll("[data-builder-feedback], [data-dock-feedback]").forEach((feedback) => {
          feedback.hidden = true;
          feedback.textContent = "";
        });
        this.getAll("[data-builder-add]").forEach((button) => {
          button.textContent = "Add selected items to cart";
          button.removeAttribute("title");
        });
      }
      const previewCount = this.get("[data-preview-count]");
      if (previewCount) previewCount.textContent = String(count);
      this.dispatchAcrylicCount();
    }

    getCustomAcrylicCount() {
      return [...this.selected.values()].filter((item) => item.customAcrylic).length;
    }

    getPurchasableItems() {
      return [...this.selected.values()].filter((item) => !item.previewOnly && item.variantId);
    }

    getItemQuantity(item) {
      return item ? 1 : 0;
    }

    dispatchAcrylicCount() {
      document.dispatchEvent(
        new CustomEvent("chiclassy:acrylic-count", {
          detail: { count: this.getCustomAcrylicCount() }
        })
      );
    }

    makeId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    getVisibleVariantProperties(item) {
      const properties = {};
      (item.variantOptions || []).forEach(({ name, value }) => {
        if (/colou?r|颜色|色彩/i.test(name)) properties["Selected Color"] = value;
        if (/size|length|dimension|尺寸|长度/i.test(name)) properties["Selected Size"] = value;
      });
      if (!Object.keys(properties).length && item.variantTitle && item.variantTitle !== "Default Title") {
        properties["Selected Variant"] = item.variantTitle;
      }
      return properties;
    }

    async addToCart() {
      if (!this.getPurchasableItems().length || this.cartBusy) return;
      this.cartBusy = true;
      this.getAll("[data-builder-add]").forEach((element) => { element.disabled = true; });
      const button = this.get("[data-builder-add]");
      const originalText = button?.textContent || "Add Selected Items to Cart";

      try {
        if (button) {
          button.disabled = true;
          button.textContent = "Adding…";
        }

        const root = window.Shopify?.routes?.root || "/";
        const buildId = this.makeId();
        const customItems = this.getPurchasableItems().filter((item) => item.customAcrylic);
        const regularItems = this.getPurchasableItems().filter((item) => !item.customAcrylic);

        for (let index = 0; index < customItems.length; index += 1) {
          const item = customItems[index];
          if (!(item.cartFormData instanceof FormData) && !(item.artworkFile instanceof File)) {
            throw new Error("One custom acrylic is missing its PNG artwork file.");
          }

          const formData = item.cartFormData instanceof FormData ? item.cartFormData : new FormData();
          formData.set("id", String(item.variantId));
          formData.set("quantity", "1");
          formData.set("properties[_Chiclassy Builder]", "Yes");
          formData.set("properties[_Chiclassy Build ID]", buildId);
          formData.set("properties[_Acrylic Slot]", String(index + 1));
          formData.set("properties[Shape]", item.shape || "Circle");
          formData.set("properties[Finish]", item.finish || "Clear");
          formData.set("properties[Template]", item.templateTitle || "Uploaded PNG");
          if (item.variantTitle && item.variantTitle !== "Default Title") {
            const variantProperty = /\d+(?:\.\d+)?\s*mm/i.test(item.variantTitle)
              ? "Selected Size"
              : "Selected Variant";
            formData.set(`properties[${variantProperty}]`, item.variantTitle);
          }
          formData.set("properties[_Preview X]", `${Number(item.artworkX ?? item.x ?? 0).toFixed(1)}%`);
          formData.set("properties[_Preview Y]", `${Number(item.artworkY ?? item.y ?? 0).toFixed(1)}%`);
          formData.set("properties[_Preview Zoom]", `${Math.round(Number(item.artworkScale || 1) * 100)}%`);
          formData.set("properties[_Builder X]", `${Number(item.x || 0).toFixed(2)}%`);
          formData.set("properties[_Builder Y]", `${Number(item.y || 0).toFixed(2)}%`);
          formData.set("properties[_Builder Rotation]", `${Number(item.rotation || 0).toFixed(2)}°`);
          formData.set("properties[_Builder Flip X]", item.flipX ? "Yes" : "No");
          formData.set("properties[_Builder Flip Y]", item.flipY ? "Yes" : "No");
          formData.set("properties[_Builder Placed]", item.placed ? "Yes" : "No");
          if (item.artworkFile instanceof File) formData.set("properties[Artwork File]", item.artworkFile, item.artworkFile.name || `acrylic-${index + 1}.png`);

          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json" },
            body: formData
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || "Unable to add the custom acrylic to cart.");
          }
        }

        if (regularItems.length) {
          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({
              items: regularItems.map((item) => ({
                id: Number(item.variantId),
                quantity: 1,
                properties: {
                  ...this.getVisibleVariantProperties(item),
                  "_Chiclassy Builder": "Yes",
                  "_Chiclassy Build ID": buildId,
                  "_Builder X": item.x.toFixed(2),
                  "_Builder Y": item.y.toFixed(2),
                  "_Builder Scale": "1.000",
                  "_Builder Rotation": item.rotation.toFixed(2),
                  "_Builder Flip X": item.flipX ? "Yes" : "No",
                  "_Builder Flip Y": item.flipY ? "Yes" : "No",
                  "_Builder Placed": item.placed ? "Yes" : "No"
                }
              }))
            })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || "Unable to add items to cart.");
          }
        }

        window.location.assign(`${root}cart`);
      } catch (error) {
        this.cartBusy = false;
        this.renderTotal();
        console.error("Chiclassy Builder cart error:", error);
        this.getAll("[data-builder-feedback], [data-dock-feedback]").forEach((feedback) => {
          feedback.hidden = !this.getPurchasableItems().length;
          feedback.textContent = error.message || "Unable to add items to cart.";
        });
        if (button) {
          button.disabled = !this.getPurchasableItems().length;
          button.textContent = this.getPurchasableItems().length ? "Try again" : "Add selected items to cart";
          if (this.getPurchasableItems().length) button.setAttribute("title", error.message || "Unable to add items to cart.");
        }
      } finally {
        if (button && button.textContent === "Adding…") button.textContent = originalText;
      }
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: this.currency
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    destroy() {
      this.catalogAbort.abort();
      clearTimeout(this.searchTimer);
      clearTimeout(this.messageTimer);
      this.root.removeEventListener("click", this.onClick);
      this.root.removeEventListener("change", this.onChange);
      this.root.removeEventListener("input", this.onInput);
      this.root.removeEventListener("pointerdown", this.onPointerDown);
      this.root.removeEventListener("pointermove", this.onPointerMove);
      this.root.removeEventListener("pointerup", this.onPointerUp);
      this.root.removeEventListener("pointercancel", this.onPointerUp);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("chiclassy:acrylic-saved", this.onAcrylicSaved);
      document.removeEventListener("chiclassy:acrylic-opened", this.onAcrylicOpened);
      document.removeEventListener("chiclassy:acrylic-closed", this.onAcrylicClosed);
      this.root.removeEventListener("chiclassy:own-charm-saved", this.onOwnCharmSaved);
      this.closeProductDetails();
      this.resizeObserver?.disconnect();
      document.removeEventListener("click", this.onOutsideFilterClick);
      document.removeEventListener("click", this.onOutsidePreviewClick, true);
      delete this.root[INSTANCE_KEY];
    }
  }

  function findRoots(scope = document) {
    if (!scope) return [];
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return [...new Set(roots)];
  }

  function initAll(scope = document) {
    findRoots(scope).forEach((root) => {
      if (root[INSTANCE_KEY]) return;
      try {
        root[INSTANCE_KEY] = new ChiclassyBuilder(root);
      } catch (error) {
        // Product cards remain visible even if a Shopify theme or asset error
        // prevents the interactive builder from initializing.
        root.querySelectorAll("[data-product-card]").forEach((card) => {
          card.hidden = false;
        });
        const message = root.querySelector("[data-builder-message]");
        if (message) {
          message.textContent = "Preview could not load. Please refresh the page.";
          message.hidden = false;
        }
        console.error("Chiclassy Builder initialization failed:", error);
      }
    });
  }

  window[GLOBAL_KEY] = { initAll };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:reorder", () => initAll(document));
  document.addEventListener("shopify:section:unload", (event) => {
    findRoots(event.target).forEach((root) => root[INSTANCE_KEY]?.destroy());
  });
})();
